/**
 * Office work-scope catalog API.
 * Includes inactive scopes and a planning usage count. The active-only
 * dropdown endpoints are unchanged.
 */

import { apiRequest } from "./axios";
import { API_PATHS } from "../../config/apiConfig";

/**
 * List every work scope for the management page.
 *
 * @returns {Promise<Array<Object>>}
 */
export async function listWorkScopes() {
  const data = await apiRequest(API_PATHS.CORE.WORK_SCOPES, null, {
    method: "GET",
  });
  return Array.isArray(data) ? data : [];
}

/**
 * Create a catalog work scope.
 *
 * @param {Object} payload - Title plus optional unit and active flag.
 * @returns {Promise<Object>} Saved row, including planning usage.
 */
export async function createWorkScopeRecord(payload) {
  return await apiRequest(API_PATHS.CORE.WORK_SCOPES, payload, {
    method: "POST",
  });
}

/**
 * Patch one catalog work scope.
 *
 * @param {number|string} id - Work scope id.
 * @param {Object} patch - Fields to update.
 * @returns {Promise<Object>} Saved row, including planning usage.
 */
export async function updateWorkScopeRecord(id, patch) {
  return await apiRequest(API_PATHS.CORE.WORK_SCOPE(id), patch, {
    method: "PATCH",
  });
}

/**
 * Read the first useful message from a failed API response.
 *
 * @param {Error} error - Axios error.
 * @param {string} fallback - Message used when the body has no detail.
 * @returns {string}
 */
export function formatApiError(error, fallback) {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (typeof data.detail === "string") return data.detail;
  if (typeof data.error === "string") return data.error;
  if (typeof data.message === "string") return data.message;
  const first = Object.values(data)[0];
  if (Array.isArray(first) && first.length) return String(first[0]);
  if (typeof first === "string") return first;
  return fallback;
}
